import { INIT_PAGINATION } from '@vindr/services/constants/common.constant';
import { SORT_ORDER } from '@vindr/services/constants/filter.constant';
import useControlModal from '@vindr/utils/hooks/useControlModal';
import { PaginationProps, SettingsContext } from '@vindr/services/interfaces';
import isDisableApprover from '#pages/dashboard/components/isDisableApprover';
import { useGetDashboardPerformByRadiologist } from '@vindr/services/actions/dashboard';
import keycloak from '@vindr/services/keycloak';
import { filterValidValue } from '@vindr/utils/helpers/common.helper';
import { extendDateOptions } from '@vindr/ui/src/components/ui/DateRangePicker';
import { createContext, useContext, useMemo, useState } from 'react';
import { ImmerHook, useImmer } from 'use-immer';

export const INIT_FILTER_PERFORMANCE_BY_RADIOLOGIST: Parameters<
  typeof useGetDashboardPerformByRadiologist
>[0] = {
  sort_field: 'total',
  sort_order: SORT_ORDER.DESC,
  ...(isDisableApprover &&
    keycloak.getUserInfo()?.id && {
      approver_ids: [keycloak.getUserInfo()?.id],
    }),
  study_date_from: extendDateOptions['This Month'][0].startOf('days').toISOString(),
  study_date_to: extendDateOptions['This Month'][1].endOf('days').toISOString(),
};

export interface PerformanceByRadioLogistContext extends SettingsContext {
  PerformanceByRadioLogistData: ReturnType<
    typeof useGetDashboardPerformByRadiologist
  >['data']['data'];
  refetch: ReturnType<typeof useGetDashboardPerformByRadiologist>['refetch'];
  editItem: PerformanceByRadioLogistContext['PerformanceByRadioLogistData'][number];
  filter: Parameters<typeof useGetDashboardPerformByRadiologist>[0];
  setFilter: ImmerHook<PerformanceByRadioLogistContext['filter']>[1];
}

const PerformanceByRadioLogistContext = createContext({});
const PerformanceByRadioLogistProvider = ({ children }: { children: React.ReactNode }) => {
  const [editItem, setEditItem] = useState<any>(null);
  const [stateAction, setStateAction] = useState<'create' | 'edit'>('create');
  const [filter, setFilter] = useImmer<Parameters<typeof useGetDashboardPerformByRadiologist>[0]>(
    INIT_FILTER_PERFORMANCE_BY_RADIOLOGIST
  );
  const [pagination, setPagination] = useImmer<PaginationProps>(INIT_PAGINATION);

  const { openModal, setCloseModal, setOpenAddModal, setOpenEditModal } = useControlModal(
    setEditItem,
    setStateAction
  );

  const { data, isFetching, refetch } = useGetDashboardPerformByRadiologist({
    limit: pagination.size,
    offset: (pagination.page - 1) * pagination.size,
    ...filterValidValue(filter),
  });

  const { count: total, data: PerformanceByRadioLogistData } = data || {};

  const memoizedValue = useMemo(
    () => ({
      editItem,
      filter,
      isLoading: isFetching,
      refetch,
      openModal,
      pagination,
      setCloseModal,
      setEditItem,
      setFilter,
      setOpenAddModal,
      setOpenEditModal,
      setPagination,
      stateAction,
      total,
      PerformanceByRadioLogistData,
    }),
    [
      editItem,
      filter,
      isFetching,
      refetch,
      openModal,
      pagination,
      setCloseModal,
      setEditItem,
      setFilter,
      setOpenAddModal,
      setOpenEditModal,
      setPagination,
      stateAction,
      total,
      PerformanceByRadioLogistData,
    ]
  );

  return (
    <PerformanceByRadioLogistContext.Provider value={memoizedValue}>
      {children}
    </PerformanceByRadioLogistContext.Provider>
  );
};
PerformanceByRadioLogistProvider.displayName = 'PerformanceByRadioLogistProvider';

export const usePerformanceByRadioLogist = () => {
  const context = useContext(PerformanceByRadioLogistContext);

  if (!context) {
    throw new Error(
      'usePerformanceByRadioLogist must be used within a PerformanceByRadioLogistProvider'
    );
  }

  return context as PerformanceByRadioLogistContext;
};

export default PerformanceByRadioLogistProvider;
